import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

/**
 * Async Thunks for Document API Calls
 */

// Create a new document
export const createDocument = createAsyncThunk(
  'documents/create',
  async ({ templateId, initialAnswers = {}, requestId = null }, { rejectWithValue }) => {
    try {
      const response = await api.post('/documents', {
        templateId,
        initialAnswers,
        requestId, // Idempotency key
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Update document answers
export const updateDocument = createAsyncThunk(
  'documents/update',
  async ({ documentId, answers }, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/documents/${documentId}`, {
        answers,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Complete document
export const completeDocument = createAsyncThunk(
  'documents/complete',
  async ({ documentId, contentHtml = null, answers = null, title = null }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/documents/${documentId}/complete`, {
        contentHtml, // Frontend'den render edilmiş content HTML
        answers, // Frontend'den gelen answers (birebir kaydedilecek)
        title, // Frontend'den gelen title (opsiyonel)
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Get document by ID
export const getDocument = createAsyncThunk(
  'documents/getById',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/documents/${documentId}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Get user's documents
export const getDocuments = createAsyncThunk(
  'documents/getAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/documents', { params });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Delete document
export const deleteDocument = createAsyncThunk(
  'documents/delete',
  async (documentId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/documents/${documentId}`);
      return { documentId, message: response.data.message };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Render preview
export const renderPreview = createAsyncThunk(
  'documents/renderPreview',
  async ({ documentId, answers = null }, { rejectWithValue }) => {
    try {
      const params = answers ? { answers: JSON.stringify(answers) } : {};
      const response = await api.get(`/documents/${documentId}/preview`, { params });
      return response.data.data.contentHtml;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message }
      );
    }
  }
);

// Download PDF
export const downloadPDF = createAsyncThunk(
  'documents/downloadPDF',
  async ({ documentId, documentTitle }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/documents/${documentId}/pdf`, {
        responseType: 'blob',
        timeout: 600000, // 600 seconds (10 minutes) for large PDF files
      });
      
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data;
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Sanitize filename: Keep Turkish characters, only remove invalid filesystem characters
      const sanitizedTitle = (documentTitle || 'document')
        .replace(/[<>:"/\\|?*]/g, '_') // Remove invalid filesystem characters
        .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
        .trim();
      link.download = `${sanitizedTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Return simple object, not the blob
      return { documentId, success: true };
    } catch (error) {
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return rejectWithValue({ 
          message: 'PDF indirme işlemi zaman aşımına uğradı. Dosya çok büyük olabilir, lütfen tekrar deneyin.' 
        });
      }
      
      // Handle blob error response
      let errorMessage = 'PDF indirilirken bir hata oluştu';
      
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            errorMessage = json.message || errorMessage;
          } catch {
            // If parsing fails, use default message
            errorMessage = 'PDF indirilirken bir hata oluştu';
          }
        } else if (typeof error.response.data === 'object' && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Always return a plain object, never a Blob
      return rejectWithValue({ message: errorMessage });
    }
  }
);

// Download DOCX
export const downloadDOCX = createAsyncThunk(
  'documents/downloadDOCX',
  async ({ documentId, documentTitle }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/documents/${documentId}/docx`, {
        responseType: 'blob',
        timeout: 600000, // 600 seconds (10 minutes) for large DOCX files
      });
      
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data;
      
      // Create blob URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Sanitize filename: Keep Turkish characters, only remove invalid filesystem characters
      const sanitizedTitle = (documentTitle || 'document')
        .replace(/[<>:"/\\|?*]/g, '_') // Remove invalid filesystem characters
        .replace(/\s+/g, ' ') // Normalize multiple spaces to single space
        .trim();
      link.download = `${sanitizedTitle}.docx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      // Return simple object, not the blob
      return { documentId, success: true };
    } catch (error) {
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        return rejectWithValue({ 
          message: 'DOCX indirme işlemi zaman aşımına uğradı. Dosya çok büyük olabilir, lütfen tekrar deneyin.' 
        });
      }
      
      // Handle blob error response
      let errorMessage = 'DOCX indirilirken bir hata oluştu';
      
      if (error.response?.data) {
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const json = JSON.parse(text);
            errorMessage = json.message || errorMessage;
          } catch {
            // If parsing fails, use default message
            errorMessage = 'DOCX indirilirken bir hata oluştu';
          }
        } else if (typeof error.response.data === 'object' && error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Always return a plain object, never a Blob
      return rejectWithValue({ message: errorMessage });
    }
  }
);

/**
 * Document Slice
 */
const documentSlice = createSlice({
  name: 'documents',
  initialState: {
    currentDocument: null,
    documents: [],
    previewContent: null,
    loading: false,
    saving: false,
    error: null,
    lastSavedAt: null,
    syncStatus: 'synced', // 'synced' | 'pending' | 'failed'
    downloadingPDF: false,
    downloadingDOCX: false,
  },
  reducers: {
    // Clear current document
    clearCurrentDocument: (state) => {
      state.currentDocument = null;
      state.previewContent = null;
    },
    // Set answers locally (for immediate UI update)
    setAnswers: (state, action) => {
      if (state.currentDocument) {
        state.currentDocument.answers = action.payload;
      }
    },
    // Set sync status
    setSyncStatus: (state, action) => {
      state.syncStatus = action.payload;
    },
    // Set last saved at
    setLastSavedAt: (state, action) => {
      state.lastSavedAt = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Create document
    builder
      .addCase(createDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDocument.fulfilled, (state, action) => {
        state.loading = false;
        // API response: { success: true, data: { document } }
        // action.payload = response.data.data = { document }
        // Extract document from payload if needed
        state.currentDocument = action.payload.document || action.payload;
        state.error = null;
      })
      .addCase(createDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update document
    builder
      .addCase(updateDocument.pending, (state) => {
        state.saving = true;
        state.syncStatus = 'pending';
        state.error = null;
      })
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.saving = false;
        state.syncStatus = 'synced';
        // API response: { success: true, data: { document } }
        // action.payload = response.data.data = { document }
        // Extract document from payload if needed
        state.currentDocument = action.payload.document || action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.error = null;
      })
      .addCase(updateDocument.rejected, (state, action) => {
        state.saving = false;
        state.syncStatus = 'failed';
        state.error = action.payload;
      });

    // Complete document
    builder
      .addCase(completeDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(completeDocument.fulfilled, (state, action) => {
        state.loading = false;
        // API response: { success: true, data: { document } }
        // action.payload = response.data.data = { document }
        // Extract document from payload if needed
        state.currentDocument = action.payload.document || action.payload;
        state.error = null;
      })
      .addCase(completeDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get document
    builder
      .addCase(getDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocument.fulfilled, (state, action) => {
        state.loading = false;
        // API response: { success: true, data: { document } }
        // action.payload = response.data.data = { document }
        // Extract document from payload if needed
        state.currentDocument = action.payload.document || action.payload;
        state.error = null;
      })
      .addCase(getDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Get documents
    builder
      .addCase(getDocuments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = Array.isArray(action.payload) ? action.payload : (action.payload.documents || []);
        state.error = null;
      })
      .addCase(getDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete document
    builder
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
        // Silinen dokümanı documents array'inden kaldır
        state.documents = state.documents.filter(
          (doc) => doc._id !== action.payload.documentId
        );
        // Eğer silinen doküman currentDocument ise, onu da temizle
        if (state.currentDocument && state.currentDocument._id === action.payload.documentId) {
          state.currentDocument = null;
        }
        state.error = null;
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Render preview
    builder
      .addCase(renderPreview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(renderPreview.fulfilled, (state, action) => {
        state.loading = false;
        state.previewContent = action.payload;
        state.error = null;
      })
      .addCase(renderPreview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Download PDF
    builder
      .addCase(downloadPDF.pending, (state) => {
        state.downloadingPDF = true;
        state.error = null;
      })
      .addCase(downloadPDF.fulfilled, (state) => {
        state.downloadingPDF = false;
        state.error = null;
      })
      .addCase(downloadPDF.rejected, (state, action) => {
        state.downloadingPDF = false;
        state.error = action.payload;
      });

    // Download DOCX
    builder
      .addCase(downloadDOCX.pending, (state) => {
        state.downloadingDOCX = true;
        state.error = null;
      })
      .addCase(downloadDOCX.fulfilled, (state) => {
        state.downloadingDOCX = false;
        state.error = null;
      })
      .addCase(downloadDOCX.rejected, (state, action) => {
        state.downloadingDOCX = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentDocument, setAnswers, setSyncStatus, setLastSavedAt } = documentSlice.actions;
export default documentSlice.reducer;

